import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';
import {Observable, of, Subject} from 'rxjs';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {catchError, concatMap, filter, map, take, takeUntil} from 'rxjs/operators';

import {SEND_SMS_FAILED_STATUS_CODE, User, UserMfaConfig, UserMfaConfigName} from '@app/core/model/user';
import {IntegrationService} from '@app/core/service/integration.service';
import {AuthenticatedUserService, DEFAULT_DISABLE_RESEND_CODE_BTN_DURATION} from '@app/core/service/authenticated-user.service';
import {MESSAGES} from '@app/core/model/messages';
import {environment} from '@env/environment';
import {copyData} from '@app/core/model/integration-models';
import * as authAction from '@app/views/common/auth.actions';
import {Store} from '@ngrx/store';
import {State} from '@app/app.reducers';
import {getDigitsOnly, US_PHONE_NUMBERS_WITHOUT_COUNTRY_CODE_LENGTH} from '@app/core/utils/other-utils';
import {AuditUserActivity, AuditUserActivityActions} from '@app/core/model/audit-user-activity.model';
import {AuditUserActivityService} from '@app/core/service/audit-user-activity.service';

@Component({
    selector: 'ngc-verify-phone-dialog-component',
    templateUrl: './verify-phone-dialog.component.html',
    styleUrls: ['./verify-phone-dialog.component.scss']
})
export class VerifyPhoneDialogComponent implements OnInit, OnDestroy {

    phone: string;
    isSpinnerActive: boolean;
    recoveryCodes: string[];
    formGroup: UntypedFormGroup;
    activeView: SmsMfaDialogViews;
    isLoginMFASetup: boolean;
    isVendor: boolean;
    disableResendCodeBtnSecondsEndDate: number;
    disableResendCodeBtnTimer: number;

    readonly VERIFICATION_FAILED_MESSAGE = 'Verification failed.';
    readonly PASSWORD_VERIFICATION_FAILED_MESSAGE = 'The current password you entered is incorrect.';
    readonly SMS_MFA_DIALOG_VIEWS = SmsMfaDialogViews;
    private readonly DEFAULT_DISABLE_RESEND_CODE_BTN_DURATION = DEFAULT_DISABLE_RESEND_CODE_BTN_DURATION;

    private unsubscribe$ = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<VerifyPhoneDialogComponent>,
        public snackBar: MatSnackBar,
        public authenticatedUserService: AuthenticatedUserService,
        private auditUserActivityService: AuditUserActivityService,
        public integrationService: IntegrationService,
        private store: Store<State>,
        private changeDetector: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) public injectedData: {
            user: User;
            orgCountry: string;
            view: SmsMfaDialogViews;
            isLoginMFASetup: boolean;
        }
    ) {
        this.isLoginMFASetup = this.injectedData.isLoginMFASetup;
        this.activeView = this.injectedData.view;
        this.isVendor = this.authenticatedUserService.isVendor(this.injectedData.user);
    }

    private initForm() {
        let controls;
        if (this.activeView === SmsMfaDialogViews.InputPhone) {
            this.phone = this.transformSavedUserMFAPhone(this.injectedData.user, this.injectedData.orgCountry);
            const validators = [
                Validators.maxLength(32),
                AuthenticatedUserService.isValidPhoneNumberValidator
            ];

            let predefinedPhone: string;
            if (this.injectedData.user.mfaEnabled) {
                validators.push(this.getNotTheSamePhoneValidator());
            } else {
                predefinedPhone = (this.injectedData.user.mfaPhone || this.injectedData.user.phone);
            }

            const prePopulatedMFAPhone = this.prePopulateMFAPhoneField(predefinedPhone);

            controls = {phone: new UntypedFormControl(prePopulatedMFAPhone, validators)};
        } else if (this.activeView === SmsMfaDialogViews.VerifyPhone) {
            if (!this.phone) {
                this.phone = this.injectedData.user.mfaPhone;
            }
            controls = {code: new UntypedFormControl(
                '',
                [
                    Validators.required,
                    Validators.minLength(4),
                    Validators.maxLength(6),
                    Validators.pattern(/^[0-9]*$/)
                ]
            )};
        } else if (this.activeView === SmsMfaDialogViews.RecoveryCodes) {

        } else if (this.activeView === SmsMfaDialogViews.VerifyPassword) {
            controls = {passwordCtrl: new UntypedFormControl('', Validators.required)};
        }
        this.formGroup = new UntypedFormGroup(controls);

        if (this.activeView === SmsMfaDialogViews.InputPhone) {
        }
    }

    private getNotTheSamePhoneValidator() {
        return (control: UntypedFormControl): { [s: string]: boolean } => {
            const oldPhoneDigitsOnly = getDigitsOnly(this.phone);
            const phoneDigitsOnly = getDigitsOnly(control?.value);
            if (oldPhoneDigitsOnly === phoneDigitsOnly) {
                return {'samePhoneNumberError': true};
            }
            return null;
        };
    }

    private transformSavedUserMFAPhone(user: User, orgCountry: string) {
        if (user.mfaPhone && user.mfaPhone.toString().startsWith('+')) {
            return user.mfaPhone;
        } else if (orgCountry
            && (orgCountry === 'US')
            && user.mfaPhone
            && (user.mfaPhone.toString().length === US_PHONE_NUMBERS_WITHOUT_COUNTRY_CODE_LENGTH)) {
            return `+1${user.mfaPhone}`;
        } else {
            return `+${user.mfaPhone}`;
        }
        // this.prePopulateMFAPhoneField();
    }

    private prePopulateMFAPhoneField(predefinedPhone?: string): string {
        return predefinedPhone
            ? AuthenticatedUserService.transformPrePopulatedMFAPhoneNumber(predefinedPhone, this.injectedData.orgCountry)
            : (this.injectedData.orgCountry === 'US') ? '+1' : '+';
    }

    doApply(): void {
        if (this.formGroup.controls.phone.valid) {
            const phoneDigitsOnly = getDigitsOnly(this.formGroup.controls.phone.value);
            if (this.phone !== phoneDigitsOnly) {
                // this.onVerifyCodeAndSave();
            }
        }
    }

    isSaveDisabled(): boolean {
        const phoneDigitsOnly = getDigitsOnly(this.formGroup.controls.phone.value);
        return(this.formGroup.controls.phone.invalid)
            || (this.phone === phoneDigitsOnly);
    }

    ngOnInit(): void {
        this.initForm();
    }

    sendUserVerificationCode(): void {
        const digitsOnly = getDigitsOnly(this.formGroup.controls.phone.value);
        this.isSpinnerActive = true;
        const sendUserVerificationCode$ = ((!environment.stsServer || this.isVendor)
            ? this.authenticatedUserService.sendUserVerificationCode(digitsOnly)
            : this.authenticatedUserService.sendUserVerificationCodeFromPassportAuthService(digitsOnly));
        sendUserVerificationCode$.pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.onRunDisableResendCodeBtnTimer();
                // this.successfullySentUserVerificationCode(digitsOnly);
                // Show verify phone view
                this.phone = this.formGroup.controls.phone.value;
                this.activeView = SmsMfaDialogViews.VerifyPhone;
                this.initForm();
                this.isSpinnerActive = false;
            },
            err => {
                const errorMessage = (err.status === SEND_SMS_FAILED_STATUS_CODE
                    ? err.message
                    : MESSAGES.UNKNOWN_ERROR
                );
                this.snackBar.open(errorMessage, 'ERROR', <MatSnackBarConfig>{duration: 2000});
                this.isSpinnerActive = false;
            });
    }

    onVerifyCode(): void {
        this.isSpinnerActive = true;
        this.formGroup.controls.code.disable();

        const updatedUser: User = copyData(this.injectedData.user);
        updatedUser.mfaPhone = this.phone;

        const verifyCodeForUserMFAPhoneChange$ = ((!environment.stsServer || this.isVendor)
            ? this.authenticatedUserService.verifyCodeForUserMFAPhoneChange(this.formGroup.controls.code.value, updatedUser)
            : this.authenticatedUserService.verifyCodeForUserMFAPhoneChangeFromPassportAuthService(this.formGroup.controls.code.value, this.phone));
        verifyCodeForUserMFAPhoneChange$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((recoveryCodes: any) => {
                this.recoveryCodes = recoveryCodes;
                this.isSpinnerActive = false;

                updatedUser.needToSetupMFA = null;
                updatedUser.mfaEnabled = true;
                this.store.dispatch(new authAction.AuthenticatedUserRefreshSuccessAction({ user: updatedUser}));

                if (this.recoveryCodes?.length > 0) {
                    this.activeView = SmsMfaDialogViews.RecoveryCodes;
                } else {
                    this.dialogRef.close(true);
                }
            },
            err => {
                this.isSpinnerActive = false;
                this.formGroup.controls.code.enable();
                this.formGroup.controls.code.setValue('');

                const errorMessage = (err.status === SEND_SMS_FAILED_STATUS_CODE
                    ? err.message
                    : this.VERIFICATION_FAILED_MESSAGE
                );
                this.snackBar.open(errorMessage, 'ERROR', <MatSnackBarConfig>{duration: 2000});
            });
    }

    onSendCodeAgain() {
        const sendUserVerificationCode$ = ((!environment.stsServer || this.isVendor)
            ? this.authenticatedUserService.sendUserVerificationCode(this.phone)
            : this.authenticatedUserService.sendUserVerificationCodeFromPassportAuthService(this.phone));
        sendUserVerificationCode$.pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.onRunDisableResendCodeBtnTimer();
            },
            err => {
                const errorMessage = (err.status === SEND_SMS_FAILED_STATUS_CODE
                    ? err.message
                    : MESSAGES.UNKNOWN_ERROR
                );
                this.snackBar.open(errorMessage, 'ERROR', <MatSnackBarConfig>{duration: 2000});
            });
    }

    onCancel() {
        this.dialogRef.close();
    }

    onCloseRecoveryCodes() {
        this.dialogRef.close(true);
    }

    onDownload() {
        if (!environment.stsServer || this.isVendor) {
            const recoveryCode = (this.recoveryCodes && this.recoveryCodes.length > 0) ? this.recoveryCodes[0] : '';
            this.authenticatedUserService.downloadRecoveryCodes(this.injectedData.user.guid, recoveryCode)
                .pipe(
                    takeUntil(this.unsubscribe$),
                    take(1),
                    filter(res => !!res)
                ).subscribe(res => {
                    this.saveFileOnComputer(res);
                });
        } else {
            const data = this.recoveryCodes.join('\n');
            const fileData = new Blob([data], {type: 'text/plain'});
            this.saveFileOnComputer(fileData);
        }
    }

    saveFileOnComputer(data: Blob) {
        this.integrationService.saveFileOnComputer(data, `${environment.customer.customerShortName}_reset_codes.txt`);
    }

    onCopyToClipboard() {
        this.snackBar.open('Copied to Clipboard' , '', <MatSnackBarConfig>{duration: 1500});
    }

    onVerifyPassword(): void {
        this.isSpinnerActive = true;
        this.formGroup.controls.passwordCtrl.disable();
        const password = this.formGroup.controls.passwordCtrl.value;
        let regenerateRecoveryCodes$: Observable<string[]>;
        if (!environment.stsServer || this.isVendor) {
            regenerateRecoveryCodes$ = this.authenticatedUserService
                .regenerateUserMFARecoveryCodes(this.injectedData.user.guid, password);
        } else {
            regenerateRecoveryCodes$ = this.authenticatedUserService
                .regenerateUserMFARecoveryCodesFromPassportAuthService(password)
                .pipe(
                    concatMap((recoveryCodes: string[]) =>
                        this.auditUserActivityService.createUserAction(<AuditUserActivity>{
                            orgGuid: this.injectedData.user.orgGuid,
                            objectId: null,
                            actionName: AuditUserActivityActions.MFA_REGENERATE_RESET_CODES
                        }).pipe(
                            map(() => recoveryCodes),
                            catchError(() => {
                                this.snackBar.open(MESSAGES.UNKNOWN_ERROR, 'ERROR', <MatSnackBarConfig>{duration: 2000});
                                return of(recoveryCodes);
                            })
                        )
                    )
                );
        }
        regenerateRecoveryCodes$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((recoveryCodes: string[]) => {
                this.recoveryCodes = recoveryCodes;
                this.activeView = SmsMfaDialogViews.RecoveryCodes;
                this.isSpinnerActive = false;
            },
            err => {
                this.isSpinnerActive = false;
                this.formGroup.controls.passwordCtrl.enable();
                this.formGroup.controls.passwordCtrl.setValue('');

                if (err.status === 403) {
                    this.snackBar.open(this.PASSWORD_VERIFICATION_FAILED_MESSAGE, 'ERROR', <MatSnackBarConfig>{duration: 2000});
                } else {
                    this.snackBar.open(this.VERIFICATION_FAILED_MESSAGE, 'ERROR', <MatSnackBarConfig>{duration: 2000});
                }
            });
    }

    private onRunDisableResendCodeBtnTimer() {
        this.disableResendCodeBtnSecondsEndDate = Math.round(new Date().getTime() / 1000) + this.DEFAULT_DISABLE_RESEND_CODE_BTN_DURATION;
        this.setDisableResendCodeBtnTimer();
        this.onDisableResendCodeBtnTimer();
    }

    private onDisableResendCodeBtnTimer() {
        if (this.disableResendCodeBtnTimer > 0) {
            this.setDisableResendCodeBtnTimer();
            setTimeout(this.onDisableResendCodeBtnTimer.bind(this), 1000);
        } else {
            this.disableResendCodeBtnTimer = 0;
        }
        this.changeDetector.detectChanges();
    }

    private setDisableResendCodeBtnTimer(): void {
        const nowSeconds = Math.round(new Date().getTime() / 1000);
        this.disableResendCodeBtnTimer = this.disableResendCodeBtnSecondsEndDate - nowSeconds;
        this.disableResendCodeBtnSecondsEndDate = this.disableResendCodeBtnSecondsEndDate > 0 ? this.disableResendCodeBtnSecondsEndDate : 0;
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}

export enum SmsMfaDialogViews {
    InputPhone= 0,
    VerifyPhone= 1,
    RecoveryCodes = 2,
    VerifyPassword = 3
};
